/*
Usage Notes:

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  {% assign skioSellingPlanGroups = product.selling_plan_groups | where: 'app_id', 'SKIO' %}
  {% if skioSellingPlanGroups.size > 0 %}
    <skio-plan-picker
      product='{{ product | json | escape }}'
      selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
      formId='{{ product_form_id }}'
      currency='{{ cart.currency.iso_code }}'
      >
    </skio-plan-picker>
    <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>
    {% render 'skio-details' %}
  {% endif %}

  Note: formId not required if element is inside of a form already

  Example variantChanged event dispatch:
  document.dispatchEvent( new CustomEvent("variantChanged", { detail: { variantId: variant.id } }) );
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

const skioStyles = css`
  /* ────────────────────────────────────────────────────────────────────
   * Sleep Doctor rebrand — Skio plan picker re-painted to match Figma
   * node 2851:5539. The AB component renders BOTH the Subscribe / One-time
   * radios AND the qty select + ATC button inside this shadow root, so
   * everything below is the canonical subscription buy-box.
   *
   * Outer card: 22px radius, 1px #DBDADD, white bg.
   *   Subscribe (selected)  → white bg, dark text, frequency + qty + ATC visible.
   *   One-time (unselected) → #F7F7F8 bg, neutral-70 text, content collapsed.
   * The render order is controlled by the subscriptionFirst attribute
   * (set by Liquid). With subscriptionFirst=true, one-time gets
   * .skio-onetime-second (order: 2) so Subscribe sits on top.
   * ──────────────────────────────────────────────────────────────────── */
  /* A shadow root does not inherit the theme's global border-box reset, so
     without this the 1px card borders push each card 2px past the host
     width and the buy-box scrolls horizontally. The previous revision hid
     this behind overflow:clip on the fieldset; now that the cards carry
     the borders themselves, the reset has to be explicit. */
  :host,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    border: 0;
    width: 100%;
    font-family: 'Lato', sans-serif;
    color: var(--neutral-100, #28272A);
  }

  .skio-onetime-second { order: 2; }
  .skio-onetime-first  { order: 1; }

  /* ── Group containers ─────────────────────────────────────────────
   * Each card owns its own 1px border and the outer radius on its
   * outward-facing edge (top card = top corners, bottom card = bottom
   * corners). The lower card is pulled up 1px so the two borders share
   * a single seam instead of stacking into a 2px line, and the selected
   * card is raised on the z-axis so its purple border paints over the
   * neighbour's neutral one along that shared edge.
   * ──────────────────────────────────────────────────────────────── */
  .skio-group-container {
    display: none;
  }
  .skio-group-container--available {
    display: block;
    position: relative;
    width: 100%;
    background-color: var(--neutral-10, #FFFFFF);
    color: var(--neutral-70, #626069);
    border: 1px solid var(--neutral-40, #DBDADD);
    transition: background-color 0.2s ease, border-color 0.2s ease;
  }
  .skio-group-container--selected {
    background-color: var(--primary-10, #FAF9FF);
    border-color: var(--primary-60, #543CDD);
    color: var(--neutral-100, #28272A);
    z-index: 1;
  }

  /* Only the one-time card ever carries a .skio-onetime-* class, and its
     order (1 or 2) is always greater than the subscription card's implicit
     order:0 — so the subscription card is the top card in both modes and
     the one-time card is always the bottom one. */
  .skio-group-container--available:not(.skio-onetime-first):not(.skio-onetime-second) {
    border-radius: 20px 20px 0 0;
  }
  .skio-onetime-first.skio-group-container--available,
  .skio-onetime-second.skio-group-container--available {
    border-radius: 0 0 20px 20px;
    margin-top: -1px;
  }
  /* Lone card — product has no Skio groups and the merchant enabled the
     empty state. The one-time card is always first in source order, so
     "no available sibling after me" means it is the only card: round all
     four corners and drop the seam pull. */
  .skio-onetime-first.skio-group-container--available:not(:has(~ .skio-group-container--available)),
  .skio-onetime-second.skio-group-container--available:not(:has(~ .skio-group-container--available)) {
    border-radius: 20px;
    margin-top: 0;
  }

  /* Skio's internal seperator divs are redundant now that each card
     carries its own border; keep them invisible so we don't double up. */
  .skio-seperator,
  .skio-seperator-first,
  .skio-seperator-second {
    display: none;
  }

  /* ── Native radio input — hidden but focusable ───────────────── */
  .skio-group-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
  }
  .skio-group-input:focus-visible ~ .skio-group-label {
    outline: 2px solid #543CDD;
    outline-offset: -2px;
  }

  /* ── Group label = the visible card content ─────────────────── */
  /* Mobile-first per CONVENTIONS.md: base rule is the mobile frame
     (node 16:1099, 14/18 title, 16px padding), the min-width:750px block
     is the desktop frame (node 16:821, 16/20 title, 16px 20px padding). */
  .skio-group-label {
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* Not pointer. The cursor property inherits, so a blanket pointer here put the
       hand cursor over every child — price, benefit copy, the divider —
       none of which do anything on click. */
    cursor: default;
    padding: 16px;
    overflow: hidden;
    color: inherit;
  }

  /* The card body is a click target only while it is unselected, where
     clicking it picks that plan. Once selected, clicking the body is a
     no-op (the label just re-activates an already-checked radio), so it
     shouldn't advertise itself as clickable. The genuinely interactive
     children — frequency select, qty buttons, ATC, disclosure — set their
     own pointer further down and are unaffected either way. */
  .skio-group-container:not(.skio-group-container--selected) .skio-group-label {
    cursor: pointer;
  }
  @media (min-width: 750px) {
    .skio-group-label {
      font-size: 16px;
      line-height: 20px;
      padding: 16px 20px;
    }
  }

  /* ── Topline: title (+ "& Save N%") on left, tag + radio on right ── */
  .skio-group-topline {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 16px;
    font-size: inherit;
    line-height: inherit;
    margin-bottom: 0;
  }

  /* Right-hand cluster: green savings tag + radio, 12px apart (Figma 16:825). */
  .skio-topline-end {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 0 0 auto;
  }

  /* ── Savings tag — Accent/70 pill, Desktop/Label/Tag 11/16 bold ── */
  .skio-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 8px;
    border-radius: 4px;
    background-color: var(--accent-70, #039884);
    color: var(--neutral-10, #FFFFFF);
    font-family: 'Lato', sans-serif;
    font-size: 11px;
    font-weight: 700;
    line-height: 16px;
    text-transform: uppercase;
    white-space: nowrap;
  }
  /* Skio renders two radios — the inline one (.skio-radio__container)
     is the legacy hidden marker. The right-aligned visible one is
     .skio-radio-icon. */
  .skio-radio__container { display: none; }

  /* No margin-left:auto — the one-time topline right-aligns its radio via
     justify-content:space-between, and on the subscription card the radio
     sits inside .skio-topline-end where an auto margin would fight the
     12px gap against the savings tag. */
  .skio-radio-icon {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    overflow: visible;
  }
  .skio-radio-icon circle {
    fill: var(--neutral-10, #FFFFFF);
    stroke: var(--neutral-70, #626069);
    stroke-width: 1;
    r: 9.5;
    transition: stroke 0.2s ease, stroke-width 0.2s ease;
  }
  .skio-group-container--selected .skio-radio-icon circle {
    fill: var(--neutral-10, #FFFFFF);
    stroke: var(--primary-60, #543CDD);
    stroke-width: 6;
    r: 7;
  }

  /* ── Title text + inline "& Save N%" ─────────────────────────── */
  .skio-group-title {
    flex: 0 1 auto;
    min-width: 0;
    font-weight: 700;
    color: inherit;
  }
  /* No margin-left: the template already emits whitespace between the group
     name and this span, so an extra 4px rendered "Subscribe  & Save 15%"
     with a visibly double-wide gap. */
  .skio-save {
    font-weight: 700;
    color: inherit;
  }
  .skio-save [skio-discount] { color: inherit; }

  /* ── Price row (subscribe = price + frequency dropdown; one-time = price alone) ── */
  .skio-price {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    margin: 0;
    color: inherit;
  }
  /* Figma renders the price in Lato Regular (Mobile/Desktop List/Large/Default),
     not Bold — 16/20 on mobile, 18/22 on desktop. */
  .skio-price > span {
    font-family: 'Lato', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    letter-spacing: 0;
    color: inherit;
    flex: 0 0 auto;
    white-space: nowrap;
  }
  @media (min-width: 750px) {
    .skio-price > span {
      font-size: 18px;
      line-height: 22px;
    }
  }

  /* ── Frequency dropdown — only visible on the selected subscription ── */
  .skio-frequency {
    flex: 1 1 auto;
    color: #626069;
    display: none;
    align-items: center;
    gap: 5px;
    padding: 8px 36px 8px 12px;
    margin: 0;
    border-radius: 8px;
    border: 1px solid #DBDADD;
    background-color: #FFFFFF;
    width: 100%;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    line-height: 18px;
    cursor: pointer;
    white-space: nowrap;
    text-overflow: ellipsis;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23626069' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7' /%3E%3C/svg%3E");
    background-position: right 12px top 50%;
    background-size: 14px;
    background-repeat: no-repeat;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
  }
  .skio-group-container--selected .skio-frequency {
    display: flex;
  }
  .skio-frequency.skio-frequency--one {
    background-image: none;
    padding-right: 12px;
  }
  .skio-frequency span {
    text-transform: lowercase;
  }

  /* ── Group content (qty + ATC + benefits + details) — collapses on unselected.
   *
   * max-height is the collapse mechanism, so it has to clear the tallest
   * real content: ATC row (54) + three benefit rows (up to ~60 each when
   * they wrap on a narrow phone) + divider + the details summary, plus
   * five 16px gaps. 640px leaves headroom for longer merchant-authored
   * benefit copy; overflow:hidden keeps the collapsed state from painting
   * over the card below.
   * ──────────────────────────────────────────────────────────────── */
  .skio-group-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-height: 640px;
    opacity: 1;
    overflow: hidden;
    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1),
                margin-top 0.25s cubic-bezier(0.4,0,0.2,1);
  }
  /* The collapsed content is a zero-height flex item, but the parent label's
     16px gap still reserves space before it — which left a dead 16px band
     under the unselected card's price. Cancelling the gap with a negative
     margin keeps the collapse animated while matching the Figma card height. */
  .skio-group-container:not(.skio-group-container--selected) .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
    margin-top: -16px;
  }

  /* ── Benefit list — 16px checkmark + copy, 8px apart (Figma 16:843) ── */
  .skio-benefits {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  .skio-benefit {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;
    font-family: 'Lato', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: var(--neutral-100, #28272A);
  }
  @media (min-width: 750px) {
    .skio-benefit {
      font-size: 18px;
      line-height: 22px;
    }
  }
  /* Box is sized inline from the block's icon_width / icon_height settings.
     The 3px nudge keeps the glyph optically centred on the first text line
     (Figma wraps the icon in a pt-[3px] box on the multi-line item).
     Tinting rides on the color property: the theme authors its SVG assets with
     fill/stroke set to currentColor, so setting color on this wrapper
     recolours the glyph. Uploaded images keep their own colours. */
  .skio-benefit__icon {
    flex: 0 0 auto;
    display: block;
    margin-top: 3px;
  }
  .skio-benefit__icon svg,
  .skio-benefit__icon img {
    width: 100%;
    height: 100%;
    display: block;
  }
  /* Targeted by class rather than a .skio-benefit span descendant selector,
     so the rule can't also grab a nested element inside a merchant's
     rich-text markup. */
  .skio-benefit__text {
    flex: 1 1 auto;
    min-width: 0;
    color: inherit;
  }
  /* inline_richtext permits bold / italic / links. Keep a merchant-added
     link legible and on-brand inside the benefit row. */
  .skio-benefit__text a {
    color: inherit;
    text-decoration: underline;
    text-underline-position: from-font;
  }
  .skio-benefit__text a:hover {
    color: var(--primary-60, #543CDD);
  }

  /* ── Divider above the "How do subscriptions work?" row ─────────── */
  .skio-divider {
    width: 100%;
    height: 1px;
    flex: 0 0 auto;
    background-color: var(--primary-30, #CBC4F6);
  }

  /* ── ATC row: qty pill + submit button ───────────────────────── */
  .skio-atc-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    width: 100%;
  }

  /* Quantity pill — same visual spec as the section's standalone
     quantity_selector block: white bg, 1px #DBDADD border, 150px
     pill radius, 12×20 padding, 16px gap between minus / value /
     plus. Buttons are 10×10 (icon-sized hit area); the visible value
     is Lato Regular 16/20 #28272A. */
  .skio-quantity-control {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    padding: 12px 20px;
    border: 1px solid #DBDADD;
    border-radius: 150px;
    background-color: #FFFFFF;
  }

  .skio-quantity-btn {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    width: 10px;
    height: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #28272A;
    flex: 0 0 auto;
    transition: opacity 0.15s ease;
  }
  .skio-quantity-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .skio-quantity-btn svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .skio-quantity-value {
    font-family: 'Lato', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: var(--neutral-100, #28272A);
    text-align: center;
    min-width: 14px;
  }

  @media (min-width: 750px) {
    .skio-quantity-value {
      font-size: 16px;
      line-height: 20px;
    }
  }

  /* ── Submit (Add to Cart) — pill in the section's CTA color.
   *
   * Reads the same --btn-bg / --btn-fg / --btn-bg-hover / --btn-fg-hover
   * custom-property chain that assets/buttons.css uses for the regular
   * .btn classes. Setting btn--{color} on the <skio-plan-picker> host
   * element from outside (via product-skio.liquid) cascades those vars
   * into the shadow root because CSS custom properties inherit across
   * shadow boundaries. Hardcoded fallbacks keep the purple default when
   * no color class is applied. */
  button.product-form__submit,
  button.add-to-cart {
    flex: 1 1 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: var(--btn-bg, #543CDD);
    color: var(--btn-fg, #FFFFFF);
    border: 0;
    border-radius: 150px;
    /* Figma specs 80px side padding, but the button is a flex child that
       fills the row, so the padding only ever sets its min-content width.
       Keeping it modest (and min-width:0) stops the qty pill + button from
       overflowing the card on a 320–360px phone; the label still renders
       centred and full-width exactly as designed. */
    padding: 14px 16px;
    min-width: 0;
    white-space: nowrap;
    font-family: 'Lato', sans-serif;
    font-weight: 700;
    font-size: 18px;
    line-height: 26px;
    text-transform: none;
    letter-spacing: 0;
    box-shadow: none;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
    min-height: 54px;
    width: auto;
  }
  @media (min-width: 750px) {
    button.product-form__submit,
    button.add-to-cart {
      padding: 14px 40px;
    }
  }
  button.product-form__submit:hover,
  button.add-to-cart:hover {
    background-color: var(--btn-bg-hover, var(--btn-bg, #2E1B98));
    color: var(--btn-fg-hover, var(--btn-fg, #FFFFFF));
  }
  button.product-form__submit:disabled,
  button.add-to-cart:disabled {
    background-color: #DBDADD;
    color: #626069;
    cursor: not-allowed;
  }
  button.product-form__submit span {
    color: inherit;
  }

  /* .skio-group-content is now a 16px-gapped flex column, so the legacy
     margin-top on the details and its summary would double the spacing
     below the divider. Both are zeroed and the gap owns it. */
  .skio-details {
    --text-color: #59616E;
    --text-color-secondary: #888;

    user-select: none;
    -webkit-user-select: none;
    margin-top: 0;

    summary {
      color: var(--neutral-70, #626069);
      font-family: 'Lato', sans-serif;
      font-size: 14px;
      font-weight: 400;
      line-height: 22px;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 0;
      list-style: none;
      /* Explicit: a <summary> gets cursor:default from the UA sheet, and
         it used to look clickable only by inheriting the label's pointer. */
      cursor: pointer;
    }

    summary svg {
      flex: 0 0 auto;
      width: 24px;
      height: 22px;
      color: var(--neutral-70, #626069);
    }
  }

  .skio-details summary::-webkit-details-marker,
  .skio-details summary::marker {
    display: none;
    color: #fff !important;
  }

  .skio-details summary span {
    font-size: 0.9em;
    display: flex;
    padding: .5em 0;
    cursor: pointer;
    align-items: center;
    gap: 10px;
    text-decoration: underline;
    margin-top: -40px;
    color: #5f3ebc;
  }

  @keyframes fadeInDown {
    0% {
      opacity: 0;
      transform: translateY(-15px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
  .skio-details[open] > .skio-details--content {
    animation-name: fadeInDown;
    animation-duration: 0.3s;
  }

  .skio-details--content {
    position: fixed;
    z-index: 4;
    padding: 1em;
    border-radius: 5px;
    top: 134px;
    background: rgba(49, 66, 106, 0.8);
    width: 100dvw;
    height: calc(100dvh - 134px);
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .skio-details--content-inner {
    padding: 40px;
    margin: 0 auto;
    max-height: 528px;
    height: auto;
    background: #fff;
    max-width: 320px;
    width: 100%;
    margin: 1rem;
    gap: 16px;
    border-radius: 12px;
    overflow-y: auto;
    position: relative;

    .skio-close-details {
      position: absolute;
      border: none;
      background: transparent;
      position: absolute;
      top: 8px;
      right: 2px;
      cursor: pointer;
    }
  }
  .skio-details--content-inner ul {
    padding: 0;
    margin: 0;
  }

  .skio-details ul li {
    display: flex;
    align-items: flex-start;
    gap: .75em;

    margin-bottom: 1em;
  }

  .skio-details .skio-content {
    display: flex;
    flex-direction: column;
  }

  .skio-details .skio-content p {
    font-size: 0.9em;

    margin-top: 0;
    margin-bottom: 0;

    letter-spacing: 0;
    line-height: 1.5;

    color: var(--text-color);
  }

  .skio-details ul li small {
    font-size: 0.7em;
    color: var(--text-color-secondary);
  }

  .skio-details .skio-icon {
    display: flex;

    width: 2.25em;
    height: 2.25em;

    color: var(--text-color);
    background: #f8f8f8;
    border-radius: 100%;

    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  .skio-details .skio-icon svg {
    width: 1.25em;
    height: 1.25em;

    color: inherit;
  }

  .skio-details--footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.9em;
  }

  .skio-details--footer a {
    color: var(--text-color);
  }

  .skio-manage-link {
    text-decoration: underline;
  }

  /* Legacy AB-component rules that duplicated (and overrode, via the
     #ProductSubmitButton ID selector) the rebrand styles above were
     removed. The earlier rules in this template now own every layout
     and color decision for .skio-atc-row, .skio-quantity-select,
     button.product-form__submit / button.add-to-cart, and the
     seperator divs. */
  .powered-by-skio {
    font-size: 0.8em;
    display: flex;
    text-decoration: none;
    align-items: center;
    gap: 3px;
  }
`

/* Benefit icon used only when the picker is mounted without the block's
   icon settings at all. The schema defaults icon_svg to this same asset
   (icon-func-checkmark.svg), so in the theme this is a safety net rather
   than the usual path. Vector is the Figma checkmark (node 16:844), with
   fill switched to currentColor so the icon-colour setting drives it the
   same way it drives the theme's own SVG assets. */
const DEFAULT_BENEFIT_ICON =
  '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M14.6705 2.65719C15.0344 2.91755 15.1081 3.41003 14.8352 3.75718L7.42352 13.1857C7.28808 13.358 7.08445 13.4697 6.86024 13.4947C6.63603 13.5197 6.4109 13.4558 6.23749 13.3179L1.29634 9.38933C0.946935 9.11153 0.899727 8.61609 1.1909 8.28273C1.48207 7.94937 2.00135 7.90433 2.35075 8.18213L6.62598 11.5812L13.5176 2.81433C13.7905 2.46718 14.3067 2.39683 14.6705 2.65719Z" fill="currentColor"/>' +
  '</svg>'

/* ── SD-26-541 analytics ──────────────────────────────────────────────────
 * Two rails, matching how the rest of the theme is wired:
 *
 *   Behavioural  → window.dataLayer (GTM), subscription_* namespace. Same
 *                  pattern as the medicare_modal / insurance_modal snippets.
 *                  These fire live.
 *
 *   Ecommerce    → window.ElevarDataLayer (the Elevar app embed, enabled in
 *                  settings_data.json). Elevar's Shopify listener picks up
 *                  native <form> adds automatically — which covers the sticky
 *                  add-to-cart bar, since that posts a real form with a
 *                  selling_plan hidden input. It does NOT see this component's
 *                  fetch('/cart/add.js'), so subscription adds made from the
 *                  card itself are currently missing from ecommerce reporting.
 *                  pushElevarAddToCart() closes that gap.
 *
 * Left disabled deliberately, following the identical scaffold in
 * assets/addon-flow.js: Elevar rejects unknown or mistyped fields, so the
 * payload wants a pass in the Elevar debugger before it starts writing to
 * production GA4 ecommerce data. Flip `enabled` to true once verified —
 * nothing else needs to change. The GTM behavioural events above are
 * unaffected by this flag and report from the moment this ships. */
const SUBSCRIPTION_ELEVAR_TRACKING = { enabled: false }

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object }, // required
    productHandle: { type: String }, // optional (unless product isn't passed, then required)
    key: { type: String }, // optional, defaults to product.id; identifier for this instance of the Skio plan picker
    quantityMin: { type: Number },
    quantityMax: { type: Number },
    quantityStep: { type: Number },
    quantityValue: { type: Number },

    formId: { type: String }, // optional; if passed, used to connect input fields to form
    needsFormId: { type: Boolean }, // optional, defaults to false; if true, element needs to be passed a formId, else it searches for a form
    isMobile: { type: Boolean },

    subscriptionFirst: { type: Boolean }, // optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, // optional, defaults to false; if true, auto-selects subscription on page load
    discountFormat: { type: String }, // optional, defaults to percent; can also pass "fixed"

    currency: { type: String }, // optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String }, // optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {}, // placeholder for object

    externalPriceSelector: { type: String }, // optional, used to update the external price

    externalPriceSelectorWithCurrency: { type: String }, // optional, used to update the external price

    selectedVariant: { type: Object }, // placeholder for data
    skioSellingPlanGroups: {}, // placeholder for data
    availableSellingPlanGroups: {}, // placeholder for data
    selectedSellingPlanGroup: {}, // placeholder for data
    selectedSellingPlan: {}, // placeholder for data
    selectedSellingPlanId: {}, // placeholder for data

    defaultFrequency: {}, // placeholder for data

    showAddToCartButton: { type: Boolean },

    /* Sleep Doctor (SD-26-541): subscription buy-box marketing copy, passed
       down from the skio_plan_picker block settings by product-skio.liquid.
       `benefits` is a JSON array of strings shown as a checkmark list on the
       selected subscription card; the token {discount} in any entry is
       replaced with the live selling-plan discount so the copy can never
       drift from the actual percentage. `showSavingsTag` toggles the green
       Accent/70 tag in the card topline. */
    benefits: { type: Array },
    showSavingsTag: { type: Boolean },

    /* Benefit icon, also from the skio_plan_picker block settings.
       `benefitIcon` is SVG markup already resolved by inline_asset_content
       in Liquid — the component lives in a shadow root, so the asset can't
       be pulled in from here. An empty string means the merchant chose
       "None"; undefined means the picker was mounted without the settings
       at all, which falls back to the built-in checkmark.
       `benefitIconImage` is an uploaded override and wins when set.
       `benefitIconColor` empty means "match the benefit text colour". */
    benefitIcon: { type: String },
    benefitIconImage: { type: String },
    benefitIconWidth: { type: Number },
    benefitIconHeight: { type: Number },
    benefitIconColor: { type: String },

    /* Hides the "How do subscriptions work?" disclosure and its divider.
       Negative on purpose — see the comment on howItWorks() in render(). */
    hideHowItWorks: { type: Boolean },

    meta: {}
  }

  static styles = skioStyles

  constructor () {
    super()
    this.product = null
    this.isMobile = false
    this.selectedVariant = null
    this.quantityMin = 1
    this.quantityMax = 99
    this.quantityStep = 1
    this.quantityValue = 1

    this.productHandle = null

    this.purchaseOption = 'onetime'

    this.key = null
    this.formId = null
    this.needsFormId = false

    this.skioSellingPlanGroups = []
    this.availableSellingPlanGroups = []

    this.selectedSellingPlanGroup = null
    this.selectedSellingPlan = null
    this.selectedSellingPlanId = null

    this.startSubscription = true
    this.subscriptionFirst = true

    this.skioMainProduct = true

    this.discountFormat = 'percent'

    this.externalPriceSelector = '[skio-external-price]'

    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]'

    this.currency = 'USD'
    this.language = 'en-US'
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency
    })

    this.defaultFrequency = null

    this.showAddToCartButton = false

    /* Empty by default so a picker mounted without the SD-26-541 block
       settings renders exactly as it did before (no benefit list, no tag). */
    this.benefits = []
    this.showSavingsTag = false

    /* benefitIcon is deliberately left undefined — see the property
       declaration: undefined and '' mean different things here. The rest
       carry the Figma defaults so a bare mount still matches the design. */
    this.benefitIconImage = ''
    this.benefitIconWidth = 16
    this.benefitIconHeight = 16
    this.benefitIconColor = '#543CDD'

    this.hideHowItWorks = false

    /* Empty string, not {}. Both the bundle-mode branch in
       connectedCallback and the guard in render() test `this.meta !== ''`
       to mean "this product has a skio.data bundle metafield". The old {}
       default failed that test whenever the meta attribute was absent, so
       JSON.parse({}) threw on "[object Object]" and aborted
       connectedCallback. product-skio.liquid always emits the attribute,
       which is why this stayed latent — but any other mount point would
       have hit it. */
    this.meta = ''
  }

  connectedCallback () {
    super.connectedCallback()

    /* Seed `this.quantity` from the attribute-driven `quantityValue`
       (falling back to `quantityMin`, then 1). The legacy <select>
       quantity UI selected its first option implicitly; the +/- pill
       needs a real numeric default so the value span renders and the
       button handlers don't increment from NaN. */
    if (this.quantity === undefined || this.quantity === null || Number.isNaN(this.quantity)) {
      const seed = Number(this.quantityValue);
      this.quantity = Number.isFinite(seed) && seed > 0 ? seed : (Number(this.quantityMin) || 1);
    }

    if (this.startSubscription === true) {
      this.purchaseOption = 'subscription'
    }

    if (!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle)
    }

    if (this.needsFormId && this.formId === null) {
      const forms = document.querySelectorAll('form[action="/cart/add"]')
      if (forms.length > 0) {
        let form
        forms.forEach((el) => {
          if (el.hasAttribute('skio-key')) {
            if (el.getAttribute('skio-key') === this.key) form = el
          }
        })
        if (!form) form = forms[0]
        this.formId = form.id
        this.requestUpdate()
      }
    }
    const skio = this
    document.addEventListener('updatePrice', function (e) {
      skio.updateExternalPrice()
    })
    document.addEventListener('variantChanged', function (e) {
      // update variant id
      const variantId = e.detail.variantId
      const variant = skio.product.variants.find(x => x.id === variantId)
      if (variant) skio.selectedVariant = variant
      else skio.log('Unable to find variant with id: ', variantId)
      skio.requestUpdate()
    })

    /* Same failure mode as the A/B hook below: an unguarded throw here
       aborts the rest of connectedCallback. A malformed skio.data metafield
       should degrade to the normal plan picker, not take the listeners
       registered further down with it. */
    if (this.meta !== '' && typeof this.meta === 'string') {
      try {
        this.meta = JSON.parse(this.meta)
      } catch (parseError) {
        this.error('Ignoring unparseable skio.data metafield: ', parseError)
        this.meta = ''
      }
    }

    if (this.meta !== '') {
      // overwrite Add to cart button functionality
      const form = document.querySelector(`#${this.formId}`)
      if (form) {
        const atc = form.querySelector('button[name="add"][type="submit"]')
        if (atc) {
          atc.removeAttribute('type')
          atc.addEventListener('click', function (e) {
            e.stopPropagation()
            e.stopImmediatePropagation()
            e.preventDefault()
            // add to cart with the metas

            skio.addToCartMeta()
          })
        }
      }
    }

    if (!this.isMobile) {
      const skioSticky = document.querySelector('skio-plan-picker-add-to-cart')
      if (skioSticky) {
        skioSticky.addEventListener('skio-sticky-select-selling-plan-group', function (e) {
          if (e.detail.group === null) {
            skio.selectSellingPlanGroup(null, false)
          } else {
            skio.selectSellingPlanGroup(e.detail.group, false)
            skio.selectSellingPlan(e.detail.selling_plan, e.detail.group, false)
          }
        })
      }

      const SkioStickyMobile = document.querySelectorAll('skio-plan-picker')
      if (SkioStickyMobile) {
        SkioStickyMobile.forEach((option) => {
          option.addEventListener('skio-sticky-mobile-select-selling-plan-group', function (e) {
            if (e.detail.group === null) {
              skio.selectSellingPlanGroup(null, false)
            } else {
              skio.selectSellingPlanGroup(e.detail.group, false)
              skio.selectSellingPlan(e.detail.selling_plan, e.detail.group, false)
            }
          })
        })
      }
    } else {
      const SkioPDP = document.querySelectorAll('skio-plan-picker')
      SkioPDP.forEach((option) => {
        option.addEventListener('skio-pdp-select-selling-plan-group', function (e) {
          if (e.detail.group === null) {
            skio.selectSellingPlanGroup(null, false)
          } else {
            skio.selectSellingPlanGroup(e.detail.group, false)
            skio.selectSellingPlan(e.detail.selling_plan, e.detail.group, false)
          }
        })
      })
      // if it's mobile we receive the pdp skio readings
    }

    if (!this.isMobile) {
      setTimeout(() => {
        const initalizePlanGroup = new CustomEvent('skio-pdp-select-selling-plan-group', {
          detail: { group: this.selectedSellingPlanGroup, selling_plan: this.selectedSellingPlan },
          bubbles: true,
          composed: true
        })
        this.dispatchEvent(initalizePlanGroup)
      }, 500)
    }

    /* Hook for a retired A/B test. `.plan-picker-ab__button` no longer
       exists anywhere in the theme, so this querySelector returns null and
       the unguarded addEventListener threw on every single mount — aborting
       the rest of connectedCallback, which is where the plan-radio sync and
       the click-outside-to-close handler for the subscription details modal
       are registered. Guarded rather than deleted in case the experiment
       markup comes back. */
    const abButton = document.querySelector('.plan-picker-ab__button')
    if (abButton) {
      abButton.addEventListener('click', function (e) {
        e.preventDefault()
        skio.addToCart()
      })
    }

    document.querySelectorAll('input[name="plan"]').forEach(radio => {
      radio.addEventListener('change', function () {
        document.querySelectorAll('.plan-option').forEach(opt => opt.classList.add('collapsed'))
        this.closest('.plan-option').classList.remove('collapsed')
        // Sync Skio plan picker selection
        if (this.value === 'onetime') {
          document.querySelector('skio-plan-picker').selectSellingPlanGroup(null)
        } else {
          // Find the subscription group and select it
          const skio = document.querySelector('skio-plan-picker')
          const group = skio.availableSellingPlanGroups[0] // adjust if needed
          skio.selectSellingPlanGroup(group)
        }
      })
    })
    /* Close the "How do subscriptions work?" disclosure on an outside click.
       Two reasons the previous version could never work, on top of never
       being reached (see the A/B hook above):
         1. The <details> lives in this component's shadow root, so
            document.querySelectorAll('details') never found it — and the
            array was captured at connectedCallback time, before Lit had
            rendered anything into that root at all.
         2. A click inside a shadow root is retargeted to the host element,
            so contains(e.target) cannot distinguish inside from outside.
       Querying the shadow root lazily on each click fixes (1), and
       composedPath() sees through the shadow boundary to fix (2). */
    document.addEventListener('click', (e) => {
      const openDetails = [...(this.shadowRoot?.querySelectorAll('details[open]') || [])]
      if (openDetails.length === 0) return
      const path = e.composedPath()
      openDetails.forEach((detail) => {
        /* The disclosure's backdrop is position:fixed and fills the
           viewport, so testing the <details> element itself would count a
           backdrop click as "inside" and nothing would ever close. Stay
           open only for a click on the summary (let the native toggle own
           that) or inside the white content card; treat everything else,
           backdrop included, as a dismiss. */
        const summary = detail.querySelector('summary')
        if (summary && path.includes(summary)) return
        const card = detail.querySelector('.skio-details--content-inner')
        if (card && path.includes(card)) return
        detail.removeAttribute('open')
      })
    })

    this.log('PRODUCT', this.product)
  }

  render () {
    if (!this.product || !this.selectedVariant || this.skioSellingPlanGroups.length === 0 || this.meta !== '') return

    /* Strip a trailing ".00" from formatted prices so $267.00 → $267,
       while $267.50 stays intact. Matches the regular section's
       `money_without_trailing_zeros` Liquid filter. */
    const stripZeroes = (s) => String(s).replace(/\.00(?=\D|$)/, '');

    /* Sleep Doctor rebrand: quantity is a +/- pill (matches the section's
       standalone quantity_selector block). Inline minus / plus glyphs as
       SVG paths so we don't depend on inline_asset_content here. */
    const qtyPicker = () => html`
      <div class="skio-quantity-control" role="group" aria-label="Quantity">
        <button type="button" class="skio-quantity-btn" aria-label="Decrease quantity"
          ?disabled=${this.quantity <= this.quantityMin}
          @click=${(e) => {
            e.preventDefault();
            e.stopPropagation();
            const next = Math.max(this.quantityMin, this.quantity - this.quantityStep);
            if (next !== this.quantity) { this.quantity = next; this.requestUpdate(); }
          }}>
          <svg viewBox="0 0 10 2" fill="none" aria-hidden="true">
            <path d="M0 1 L10 1" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>
        <span class="skio-quantity-value">${this.quantity}</span>
        <button type="button" class="skio-quantity-btn" aria-label="Increase quantity"
          ?disabled=${this.quantity >= this.quantityMax}
          @click=${(e) => {
            e.preventDefault();
            e.stopPropagation();
            const next = Math.min(this.quantityMax, this.quantity + this.quantityStep);
            if (next !== this.quantity) { this.quantity = next; this.requestUpdate(); }
          }}>
          <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
            <path d="M5 0 L5 10 M0 5 L10 5" stroke="currentColor" stroke-width="2"/>
          </svg>
        </button>
      </div>
    `;

    /* SD-26-541: green Accent/70 savings tag in the subscription topline.
       Suppressed when the group carries no discount so a 0%-off plan never
       renders an empty "Save" pill. */
    const savingsTag = (group) => {
      const percent = this.discount(group.selected_selling_plan).percent;
      if (!this.showSavingsTag || percent === '0%') return html``;
      return html`<span class="skio-tag">Save ${percent}</span>`;
    };

    /* SD-26-541: checkmark benefit list on the selected subscription card.
       The {discount} token in any entry resolves to the live plan discount,
       so merchant-authored copy can't drift from the real rate. Checkmark
       path is the exported Figma vector (node 16:844). */
    /* Each benefit is an inline_richtext setting, so it arrives as a small
       inline HTML fragment (Shopify restricts these to <b>/<strong>/<i>/
       <em>/<a>/<br> and sanitizes the rest). That means it cannot go
       through Lit's text interpolation, which would escape the tags and
       print them literally.
       The pinned lit-core bundle exports no unsafeHTML directive, and
       pulling the ~2.5x larger lit-all build onto every PDP for one
       directive isn't a good trade. Lit does accept a DOM Node as a child
       value, so build the element here and hand Lit the node. */
    const richBenefit = (markup) => {
      const span = document.createElement('span');
      span.className = 'skio-benefit__text';
      span.innerHTML = markup;
      return span;
    };

    /* Blankness has to be judged on the text, not the markup: a field the
       merchant has emptied can still come back as e.g. "<strong></strong>",
       which would render a checkmark with no copy beside it. */
    const benefitText = (markup) => String(markup).replace(/<[^>]*>/g, '').trim();

    /* Icon beside each benefit line. Precedence matches the theme's
       text_with_icon block: an uploaded image beats the preset SVG, and
       "None" (empty string) renders nothing at all. Built the same way as
       the rich text above — innerHTML on a real node, since the markup is
       already-resolved SVG and lit-core has no unsafeHTML. */
    const benefitIcon = () => {
      const w = Number(this.benefitIconWidth) > 0 ? Number(this.benefitIconWidth) : 16;
      const h = Number(this.benefitIconHeight) > 0 ? Number(this.benefitIconHeight) : 16;
      let style = `width:${w}px;height:${h}px;`;
      if (this.benefitIconColor) style += `color:${this.benefitIconColor};`;

      if (this.benefitIconImage) {
        return html`<span class="skio-benefit__icon" style=${style} aria-hidden="true"
          ><img src=${this.benefitIconImage} alt="" loading="lazy"
        /></span>`;
      }
      if (this.benefitIcon === '') return html``;

      const span = document.createElement('span');
      span.className = 'skio-benefit__icon';
      span.setAttribute('aria-hidden', 'true');
      span.style.cssText = style;
      span.innerHTML = this.benefitIcon === undefined ? DEFAULT_BENEFIT_ICON : this.benefitIcon;
      return span;
    };

    const benefitsList = (group) => {
      /* Trim and drop blanks here rather than relying on Liquid to omit
         unset settings — an empty benefit would otherwise render a stray
         checkmark row with no copy. */
      const percent = this.discount(group.selected_selling_plan).percent;
      const items = (Array.isArray(this.benefits) ? this.benefits : [])
        .map((benefit) => String(benefit).trim())
        .filter((benefit) => benefitText(benefit) !== '')
        /* A plan with no discount would otherwise advertise "Save 0% on
           recurring orders". Drop any line that leans on {discount} when
           there is nothing to save — same rule the savings tag follows. */
        .filter((benefit) => percent !== '0%' || !benefit.includes('{discount}'));
      if (items.length === 0) return html``;
      return html`
        <ul class="skio-benefits">
          ${items.map((benefit) => html`
            <li class="skio-benefit">
              ${benefitIcon()}
              ${richBenefit(benefit.replace(/\{discount\}/g, percent))}
            </li>
          `)}
        </ul>
      `;
    };

    /* The "How do subscriptions work?" disclosure, plus the divider that
       separates it from the benefit list — a rule with nothing under it
       reads as a stray line, so the two are shown and hidden together.

       Gated on hideHowItWorks rather than a positive showHowItWorks
       because Lit's Boolean converter treats ANY present attribute as
       true, including showHowItWorks="false". A negative attribute is the
       native HTML idiom for this (disabled, hidden, novalidate) and means
       absent = shown, so a picker mounted without the block settings keeps
       the disclosure it had before this setting existed. product-skio.liquid
       does the inversion from the merchant-facing show_how_it_works. */
    const howItWorks = () => {
      if (this.hideHowItWorks) return html``;
      return html`
        <div class="skio-divider" role="presentation"></div>
        <details class="skio-details">
          <summary>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ai ai-ArrowRepeat"><path d="M18 2l3 3-3 3"/><path d="M6 22l-3-3 3-3"/><path d="M21 5H10a7 7 0 0 0-7 7"/><path d="M3 19h11a7 7 0 0 0 7-7"/></svg>
            
            How do subscriptions work?
          </summary>
        
          <div class="skio-details--content">
            <div class="skio-details--content-inner">
              <button class="skio-close-details" onclick="this.closest('.skio-details').removeAttribute('open')">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="12" fill="#E9ECF2"></rect>
                  <rect x="7" y="16.0911" width="12.8564" height="1.28564" rx="0.642822" transform="rotate(-45 7 16.0911)" fill="#949EB0"></rect>
                  <rect x="7.90894" y="7" width="12.8564" height="1.28564" rx="0.642822" transform="rotate(45 7.90894 7)" fill="#949EB0"></rect>
                </svg>
              </button>
              <ul>
                <li>
                  <div class="skio-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
          
                  <div class="skio-content">
                    <p>Never have to think about reordering</p>
                    <small>Stay stocked with your favorite products</small>
                  </div>
                </li>
          
                <li>
                  <div class="skio-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </div>
          
                  <div class="skio-content">
                    <p>Edit your subscription anytime</p>
                    <small>Edit products, delivery schedule and more</small>
                  </div>
                </li>
          
                <li>
                  <div class="skio-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </div>
                  
                  <div class="skio-content">
                    <p>Cancel when you want to</p>
                    <small>Easily cancel, no questions asked</small>
                  </div>
                </li>
              </ul>
        
              <div class="skio-details--footer">
                <a href="/a/account/login">Manage subscriptions</a> 
              </div>
            </div>
          </div>
        </details>
      `;
    };

    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${this.key}">
        <input ${this.formId !== null ? html`form="${this.formId}"` : ''} name="selling_plan" type="hidden" value="${this.selectedSellingPlan !== null ? this.selectedSellingPlan?.id : ''}" />
        <input ${this.formId !== null ? html`form="${this.formId}"` : ''} name="properties[Discount]" type="hidden" value="${this.selectedSellingPlan !== null ? this.discount(this.selectedSellingPlan).percent : ''}" 
          ?disabled="${this.selectedSellingPlan === null}" />
        <div class="skio-group-container ${this.product.requires_selling_plan === false ? 'skio-group-container--available' : ''} ${this.selectedSellingPlanGroup === null ? 'skio-group-container--selected' : ''} ${this.subscriptionFirst ? 'skio-onetime-second' : 'skio-onetime-first'}" skio-group-container 
          @click=${() => { this.trackToggle('onetime', null); this.selectSellingPlanGroup(null) }} >
        
          <input id="skio-one-time-${this.key}" class="skio-group-input" name="skio-group-${this.key}" type="radio" value="" 
            skio-one-time ?checked=${!!(this.startSubscription === false && this.product.requires_selling_plan === false)}>
          <div class="skio-seperator skio-seperator-first"></div>
          <label class="skio-group-label" for="skio-one-time-${this.key}">
            <div class="skio-group-topline">
              <div class="skio-radio__container">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="11" stroke="currentColor"></circle>
                  <circle class="skio-radio" cx="12" cy="12" r="5" fill="currentColor"></circle>
                </svg>
              </div>
              One-time Purchase
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="skio-radio-icon">
                <circle cx="10" cy="10" r="9.5" fill="white" stroke="#2E3038"/>
              </svg>
            </div>
            <div class="skio-price">
              <span skio-onetime-price>${stripZeroes(this.moneyFormatter.format(this.selectedVariant.price / 100))}</span>
            </div>
            <div class="skio-group-content">
                <div class="skio-atc-row">
                  ${qtyPicker()}
                  <button id="ProductSubmitButton" type="button" name="add" class="button product-form__submit" aria-haspopup="dialog" @click=${() => this.addToCart(this.quantity)} ?disabled=${!this.selectedVariant.available}>
                    <span>${this.selectedVariant.available ? 'Add to Cart' : 'Sold Out'}</span>
                  </button>
                </div>
              </div>
          </label>
        </div>
        <div class="skio-seperator skio-seperator-second"></div>
        ${this.availableSellingPlanGroups
                ? this.availableSellingPlanGroups.map((group, index) =>
                    html`
            <div class="skio-group-container skio-group-container--available ${this.selectedSellingPlanGroup != null ? 'skio-group-container--selected' : ''}" skio-group-container
              @click=${() => { this.trackToggle('subscription', group); this.selectSellingPlanGroup(group) }}>
              <input id="skio-selling-plan-group-${index}-${this.key}" class="skio-group-input" name="skio-group-${this.key}"
                type="radio" value="${group.id}" skio-selling-plan-group="${group.id}" ?checked=${this.selectedSellingPlanGroup === group} >
              <label class="skio-group-label" for="skio-selling-plan-group-${index}-${this.key}">
                <div class="skio-group-topline">
                  <div class="skio-radio__container">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="11" stroke="currentColor"></circle>
                      <circle class="skio-radio" cx="12" cy="12" r="5" fill="currentColor"></circle>
                    </svg>
                  </div>
                  <div class="skio-group-title">
                    ${group.name === 'Subscription' ? 'Subscribe' : group.name}
                    ${this.discount(group.selected_selling_plan).percent !== '0%'
                            ? html`
                      <span class="skio-save">& Save <span skio-discount>${this.discountFormat === 'percent' ? this.discount(group.selected_selling_plan).percent : this.discount(group.selected_selling_plan).amount}</span></span>
                    `
                            : html``}
                  </div>
                  <div class="skio-topline-end">
                    ${savingsTag(group)}
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" class="skio-radio-icon">
                      <circle cx="10" cy="10" r="9.5" fill="white" stroke="#2E3038"/>
                    </svg>
                  </div>
                </div>
                <div class="skio-price">
                  <span skio-subscription-price>${stripZeroes(this.price(group.selected_selling_plan))}</span>
                  <select skio-selling-plans="${group.id}" class="skio-frequency"
                    @change=${(e) => { this.trackFrequency(e.target, group); this.selectSellingPlan(e.target, group) }}>
                    ${group
                            ? group.selling_plans.map((sellingPlan) =>
                                html`
                      <option value="${sellingPlan.id}" data-value="${this.selectedSellingPlanId}" ?selected=${this.selectedSellingPlan?.id === sellingPlan.id}>
                        ${group.name === 'Subscription' ? `Delivery ${sellingPlan.name.toLowerCase()}` : `${sellingPlan.name}`}
                      </option>
                      `
                            )
                            : ''}
                  </select>
                </div>
                <div class="skio-group-content">
                  <div class="skio-atc-row">
                    ${qtyPicker()}
                    <button id="ProductSubmitButton" type="button" name="add" class="button product-form__submit" aria-haspopup="dialog" @click=${() => this.addToCart(this.quantity)} ?disabled=${!this.selectedVariant.available}>
                      <span>${this.selectedVariant.available ? 'Add to Cart' : 'Sold Out'}</span>
                    </button>
                  </div>
                  ${benefitsList(group)}
                  ${howItWorks()}
                </div>
              </label>
            </div>
          `
                )
                : ''}
      </fieldset>`
  }

  updated = (changed) => {
    if (changed.has('product') && this.product) {
      // update key
      this.key = this.key ? this.key : this.product.id

      // update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        sellingPlanGroup => sellingPlanGroup.app_id === 'SKIO'
      )

      this.skioSellingPlanGroups.forEach((group) => {
        // Sort frequencies ascending by their real interval length in days.
        // Two earlier bugs this guards against:
        //   1. Stripping ALL non-digits folded a discount in the name
        //      ("Every 30 days, save 10%" → "3010") and inverted the order.
        //   2. Comparing the raw first number ignored the unit, so a list of
        //      "3 months / 6 months / 30 days" sorted to 3, 6, 30 — leaving
        //      30 days (the shortest interval) last. Normalize to days first.
        const frequencyInDays = (name) => {
          const s = String(name).toLowerCase()
          const match = s.match(/(\d+)/)
          const n = match ? parseInt(match[1], 10) : 0
          let unitDays = 1 // day / unitless default
          if (s.includes('year')) unitDays = 365
          else if (s.includes('month')) unitDays = 30
          else if (s.includes('week')) unitDays = 7
          return n * unitDays
        }

        group.selling_plans.sort(
          (a, b) => frequencyInDays(a.name) - frequencyInDays(b.name)
        )
      })
    }

    if (changed.has('selectedVariant') && this.selectedVariant) {
      // update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      const skioSellingPlanGroups = JSON.parse(JSON.stringify(this.skioSellingPlanGroups))
      this.availableSellingPlanGroups = skioSellingPlanGroups.filter(sellingPlanGroup =>
        sellingPlanGroup.selling_plans.some(sellingPlan =>
          this.selectedVariant.selling_plan_allocations.some(
            sellingPlanAllocation => sellingPlanAllocation.selling_plan_id === sellingPlan.id
          )
        )
      )

      // update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        // update each group with a default selected_selling_plan

        this.availableSellingPlanGroups.forEach((group) => {
          group.selling_plans = group.selling_plans.filter(x => this.selectedVariant.selling_plan_allocations.find(y => y.selling_plan_id === x.id))
        })

        this.availableSellingPlanGroups.forEach(group => {
          if (this.defaultFrequency) {
            const sellingPlan = group.selling_plans.find(x => x.name.toLowerCase().includes(this.defaultFrequency.toLowerCase()))
            if (sellingPlan) group.selected_selling_plan = sellingPlan
            else group.selected_selling_plan = group.selling_plans[0]
          } else {
            group.selected_selling_plan = group.selling_plans[0]
          }
        })

        if (this.startSubscription === true || this.product.requires_selling_plan === true || this.purchaseOption === 'subscription') {
          // find a matching selling plan, or choose first available

          if (this.selectedSellingPlan === null || this.selectedSellingPlan === undefined) {
            this.selectSellingPlanGroup(this.availableSellingPlanGroups[0])
          }

          const sellingPlanName = this.selectedSellingPlan?.name

          const sellingPlanGroup = this.availableSellingPlanGroups.find(x => x.selling_plans.find(y => y.name === sellingPlanName))
          const sellingPlan = sellingPlanGroup?.selling_plans.find(y => y.name === sellingPlanName)
          if (sellingPlanName === sellingPlan?.name) {
            this.selectedSellingPlanGroup = sellingPlanGroup
            this.selectedSellingPlan = sellingPlan
          } else {
            this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0]
            this.selectedSellingPlan = this.availableSellingPlanGroups[0].selling_plans[0]
          }
        } else {
          this.selectedSellingPlan = null
          this.selectedSellingPlanGroup = null
        }
      } else {
        this.selectedSellingPlan = null
        this.selectedSellingPlanGroup = null
      }

      // update the form that was passed, if any
      this.updateForm()
    }

    /* Once the picker has real data, record that the customer was shown it
       and what it offered. Guarded internally so it only reports once. */
    this.trackWidgetViewed()
  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;')
    console.log.apply(console, args)
  }

  error = (...args) => {
    args.unshift('%c [skio]', 'color: #ff0000')
    console.error.apply(console, args)
  }

  getMetadata (field) {
    const meta = this.meta
    if (typeof meta !== 'object') {
      const fieldData = meta[field]
      if (fieldData) return fieldData
      else return ''
    }
  }

  updateExternalPrice () {
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      // this.selectedSellingPlan ? el.innerHTML = this.price(this.selectedSellingPlan) : el.innerHTML = this.money( this.selectedVariant.price);
      let priceValue, priceValuefloat

      if (this.selectedSellingPlan) {
        priceValue = this.price(this.selectedSellingPlan).replace(/[^0-9.]/g, '')
        priceValuefloat = parseFloat(priceValue).toFixed(2)
        priceValuefloat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceValuefloat)
        el.innerHTML = `${priceValuefloat.split('.')[0]}<span class="decimals">${priceValuefloat.split('.')[1]}</span>`
      } else {
        priceValue = this.money(this.selectedVariant.price).replace(/[^0-9.]/g, '')
        priceValuefloat = parseFloat(priceValue).toFixed(2)
        priceValuefloat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceValuefloat)
        el.innerHTML = `${priceValuefloat.split('.')[0]}<span class="decimals">${priceValuefloat.split('.')[1]}</span>`
      }
      const priceNumeric = parseFloat(priceValue) * 100
      el.setAttribute('data-price', priceNumeric)
    })
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      let priceValue, priceValuefloat

      if (this.selectedSellingPlan) {
        priceValue = this.price(this.selectedSellingPlan).replace(/[^0-9.]/g, '')
        priceValuefloat = parseFloat(priceValue).toFixed(2)
        priceValuefloat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceValuefloat)
        el.innerHTML = `${priceValuefloat.split('.')[0]}<span class="decimals">${priceValuefloat.split('.')[1]}</span>`
      } else {
        priceValue = this.money(this.selectedVariant.price).replace(/[^0-9.]/g, '')
        priceValuefloat = parseFloat(priceValue).toFixed(2)
        priceValuefloat = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(priceValuefloat)
        el.innerHTML = `${priceValuefloat.split('.')[0]}<span class="decimals">${priceValuefloat.split('.')[1]}</span>`
      }
      const priceNumeric = parseFloat(priceValue) * 100
      el.setAttribute('data-price', priceNumeric)
    })
  }

  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup (group, emit = true) {
    this.selectedSellingPlanGroup = group
    this.selectedSellingPlan = group?.selected_selling_plan
    this.selectedSellingPlanId = group?.selected_selling_plan?.id
    if (group) this.purchaseOption = 'subscription'
    else this.purchaseOption = 'onetime'

    if (emit) {
      if (!this.isMobile) {
        const eventSelectSellingPlanGroup = new CustomEvent('skio-pdp-select-selling-plan-group', {
          detail: { group: this.selectedSellingPlanGroup, selling_plan: this.selectedSellingPlan },
          bubbles: true,
          composed: true
        })
        this.dispatchEvent(eventSelectSellingPlanGroup)
      } else {
        const eventSelectSellingPlanGroup = new CustomEvent('skio-sticky-mobile-select-selling-plan-group', {
          detail: { group: this.selectedSellingPlanGroup, selling_plan: this.selectedSellingPlan },
          bubbles: true,
          composed: true
        })
        this.dispatchEvent(eventSelectSellingPlanGroup)
      }
    }

    // update the form that was passed, if any
    this.updateForm()

    const skioPriceUpdate = new CustomEvent('skio-price-update', {
      detail: { group: this.selectedSellingPlanGroup, selling_plan: this.selectedSellingPlan },
      bubbles: true,
      composed: true
    })
    this.dispatchEvent(skioPriceUpdate)
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan (element, group, emit = true) {
    /* `element` is either the frequency <select> (value is a STRING, because
       the option is rendered as value="${sellingPlan.id}") or a selling-plan
       object passed straight from the sticky-bar sync (id is a NUMBER, as it
       comes out of the Shopify product JSON).

       The previous strict comparison against x.id — always a number — could
       therefore never match the <select> branch: picking a new delivery
       frequency logged "couldn't find selling plan with id N" and silently
       kept the original plan, so the customer got the cadence they started
       on. Coercing both sides fixes the dropdown without disturbing the
       object branch. */
    const requestedId = element.value !== undefined ? element.value : element.id
    const sellingPlan = group.selling_plans.find(x => String(x.id) === String(requestedId))
    if (sellingPlan) {
      group.selected_selling_plan = sellingPlan
      this.selectedSellingPlanGroup = group
      this.selectedSellingPlan = sellingPlan
      this.selectedSellingPlanId = sellingPlan.id
      if (emit) {
        if (!this.isMobile) {
          const eventSelectSellingPlanGroup = new CustomEvent('skio-pdp-select-selling-plan-group', {
            detail: { group, sellingPlan },
            bubbles: true,
            composed: true
          })
          this.dispatchEvent(eventSelectSellingPlanGroup)
        } else {
          const eventSelectSellingPlanGroup = new CustomEvent('skio-sticky-mobile-select-selling-plan-group', {
            detail: { group, sellingPlan },
            bubbles: true,
            composed: true
          })
          this.dispatchEvent(eventSelectSellingPlanGroup)
        }
      }
    } else this.log("Error: couldn't find selling plan with id " + element.value + ' for variant ' + this.selectedVariant.id + ' from product ' + this.product.id + ' : ' + this.product.handle)
  }

  // Formats integer value into money value
  money (price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount (sellingPlan) {
    if (!sellingPlan) { return { percent: '0%', amount: 0 } }

    if (sellingPlan.name.includes('prepaid')) {
      const option = sellingPlan.options[0].value

      const numberPattern = /\d+/g
      const result = parseInt(option.match(numberPattern).join(''))

      const priceAdjustment = sellingPlan.price_adjustments[0]
      const discount = { percent: '0%', amount: 0 }
      const price = this.selectedVariant.price * result

      switch (priceAdjustment.value_type) {
        case 'percentage':
          discount.percent = `${priceAdjustment.value}%`
          discount.amount = Math.round(
            (price * priceAdjustment.value) / 100.0
          )
          break
        case 'fixed_amount':
          discount.percent = `${Math.round(
                        ((priceAdjustment.value * 1.0) / price) * 100.0
                    )}%`
          discount.amount = price - priceAdjustment.value
          break
        case 'price':
          discount.percent = `${Math.round(
                        (((price - priceAdjustment.value) * 1.0) /
                            price) *
                        100.0
                    )}%`
          discount.amount = price - priceAdjustment.value
          break
      }

      return discount
    } else {
      const priceAdjustment = sellingPlan.price_adjustments[0]
      const discount = { percent: '0%', amount: 0 }
      const price = this.selectedVariant.price

      switch (priceAdjustment.value_type) {
        case 'percentage':
          discount.percent = `${priceAdjustment.value}%`
          discount.amount = Math.round(
            (price * priceAdjustment.value) / 100.0
          )
          break
        case 'fixed_amount':
          discount.percent = `${Math.round(
                        ((priceAdjustment.value * 1.0) / price) * 100.0
                    )}%`
          discount.amount = priceAdjustment.value
          break
        case 'price':
          discount.percent = `${Math.round(
                        (((price - priceAdjustment.value) * 1.0) /
                            price) *
                        100.0
                    )}%`
          discount.amount = price - priceAdjustment.value
          break
      }

      return discount
    }
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price (sellingPlan, formatted = true) {
    if (sellingPlan.name.includes('prepaid')) {
      const option = sellingPlan.options[0].value
      const numberPattern = /\d+/g
      const result = parseInt(option.match(numberPattern).join(''))

      return formatted
        ? this.money((this.selectedVariant.price * result) - this.discount(sellingPlan).amount)
        : (this.selectedVariant.price * result) - this.discount(sellingPlan).amount
    } else {
      return formatted
        ? this.money(this.selectedVariant.price - this.discount(sellingPlan).amount)
        : this.selectedVariant.price - this.discount(sellingPlan).amount
    }
  }

  // If a formId was passed, appends the necessary <input> elements to the form
  updateForm () {
    if (this.formId) {
      const form = document.querySelector(`#${this.formId}`)

      if (form) {
        let sellingPlanInput = form.querySelector('[name="selling_plan"]')
        if (sellingPlanInput) {
          sellingPlanInput.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null
          sellingPlanInput.disabled = this.selectedSellingPlan?.id === undefined
        } else {
          sellingPlanInput = document.createElement('input')
          sellingPlanInput.type = 'hidden'
          sellingPlanInput.name = 'selling_plan'
          sellingPlanInput.value = this.selectedSellingPlan?.id !== undefined ? this.selectedSellingPlan?.id : null
          sellingPlanInput.disabled = this.selectedSellingPlan?.id === undefined
          form.append(sellingPlanInput)
        }

        let discountInput = form.querySelector('[name="properties[Discount]"]')
        if (discountInput) {
          discountInput.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null
          discountInput.disabled = this.selectedSellingPlan?.id === undefined
        } else {
          discountInput = document.createElement('input')
          discountInput.type = 'hidden'
          discountInput.name = 'properties[Discount]'
          discountInput.value = this.selectedSellingPlan?.id !== undefined ? this.discount(this.selectedSellingPlan).percent : null
          discountInput.disabled = this.selectedSellingPlan?.id === undefined
          form.append(discountInput)
        }
      } else {
        console.log(`Skio error: form ID is ${this.formId}, but no form with that ID was found.`)
      }
    }
  }

  // Optional functions keep if necessary

  /**
     *
     *
     */

  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  /* ── Analytics helpers (SD-26-541) ─────────────────────────────────── */

  // Common envelope for every subscription_* event.
  trackEvent (event, params) {
    try {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push(Object.assign({
        event,
        product_id: String(this.product?.id ?? ''),
        product_handle: this.product?.handle ?? '',
        product_title: this.product?.title ?? '',
        variant_id: String(this.selectedVariant?.id ?? ''),
        /* `this.isMobile` is declared but never assigned (it stays false),
           so read the viewport against the component's own 750px breakpoint
           rather than trusting it. */
        device_type: window.matchMedia('(min-width: 750px)').matches ? 'desktop' : 'mobile'
      }, params))
    } catch (trackingError) {
      /* Analytics must never break the buy box. */
      this.error('subscription tracking failed for ' + event + ': ', trackingError)
    }
  }

  // How many purchase options the customer is actually offered. 1 means there
  // is no toggle to engage with — the denominator that separates "never had a
  // choice" from "had a choice and left it on the default".
  subscriptionOptionCount () {
    const oneTime = this.product?.requires_selling_plan === false ? 1 : 0
    return oneTime + (this.availableSellingPlanGroups?.length || 0)
  }

  // Delivery frequencies on the currently selected group; 1 means the
  // frequency dropdown offers no real choice either.
  frequencyCount () {
    const group = this.selectedSellingPlanGroup || this.availableSellingPlanGroups?.[0]
    return group?.selling_plans?.length || 0
  }

  /* What the customer is actually charged per unit for the current choice:
     the plan-adjusted price on a subscription (a 15%-off plan reports 267.75,
     not the 315.00 list price), the variant price on a one-time. price() also
     multiplies prepaid plans by their billing periods, which is the real
     charge. */
  subscriptionUnitPriceCents () {
    if (this.selectedSellingPlan) {
      const planPrice = this.price(this.selectedSellingPlan, false)
      if (Number.isFinite(planPrice)) return planPrice
    }
    return this.selectedVariant?.price ?? 0
  }

  planFields (prefix, group, sellingPlan) {
    const fields = {}
    fields[prefix + 'selling_plan_id'] = String(sellingPlan?.id ?? '')
    fields[prefix + 'selling_plan_name'] = sellingPlan?.name ?? ''
    fields[prefix + 'selling_plan_group'] = group?.name ?? ''
    return fields
  }

  /* Fired once per picker after the first real render. Without it a product
     that only ever offers one purchase option is indistinguishable in the
     data from one where the customer simply never touched the toggle. */
  trackWidgetViewed () {
    if (this.subscriptionViewTracked) return
    // Bundle mode renders no picker at all, so there is nothing to have seen.
    if (this.meta !== '') return
    if (!this.product || !this.selectedVariant) return
    if (!this.skioSellingPlanGroups || this.skioSellingPlanGroups.length === 0) return

    this.subscriptionViewTracked = true
    this.trackEvent('subscription_widget_viewed', Object.assign({
      purchase_option: this.purchaseOption,
      default_purchase_option: this.purchaseOption,
      options_available: this.subscriptionOptionCount(),
      frequencies_available: this.frequencyCount()
    }, this.planFields('', this.selectedSellingPlanGroup, this.selectedSellingPlan)))
  }

  /* Deliberate toggle use only. Called from the card click handlers rather
     than from selectSellingPlanGroup(), because that method also runs
     programmatically on load to apply the startSubscription default —
     instrumenting it would count every page view as an interaction. Clicking
     the already-selected card is a no-op and is not reported. */
  trackToggle (nextOption, group) {
    const sameOption = nextOption === this.purchaseOption
    const sameGroup = (group?.id ?? null) === (this.selectedSellingPlanGroup?.id ?? null)
    if (sameOption && sameGroup) return

    this.trackEvent('subscription_option_toggled', Object.assign({
      from_option: this.purchaseOption,
      to_option: nextOption,
      options_available: this.subscriptionOptionCount(),
      frequencies_available: this.frequencyCount()
    }, this.planFields('to_', group, group?.selected_selling_plan)))
  }

  /* A <select> change event only fires on real user interaction, never on a
     programmatic .value assignment, so this needs no intent guard. Must run
     before selectSellingPlan() mutates the current selection. */
  trackFrequency (element, group) {
    const previousGroup = this.selectedSellingPlanGroup
    const previousPlan = this.selectedSellingPlan
    // Same string/number coercion as selectSellingPlan() — see the note there.
    const requestedId = element.value !== undefined ? element.value : element.id
    const nextPlan = group?.selling_plans?.find(plan => String(plan.id) === String(requestedId))
    if (!nextPlan || String(nextPlan.id) === String(previousPlan?.id)) return

    this.trackEvent('subscription_frequency_changed', Object.assign({
      frequencies_available: this.frequencyCount()
    },
    this.planFields('from_', previousGroup, previousPlan),
    this.planFields('to_', group, nextPlan)))
  }

  /* Fires for one-time adds through this widget too, tagged via
     purchase_option — those are equally invisible to Elevar's listener,
     since they go through the same fetch() below. */
  trackAddToCart (quantity, cartResponse) {
    const isSubscription = !!this.selectedSellingPlan
    const qty = Number(quantity) || 1
    const unitCents = this.subscriptionUnitPriceCents()
    this.trackEvent('subscription_add_to_cart', Object.assign({
      purchase_option: isSubscription ? 'subscription' : 'onetime',
      quantity: qty,
      options_available: this.subscriptionOptionCount(),
      frequencies_available: this.frequencyCount(),
      /* Numbers, not toFixed() strings: GA4 treats `value` as a numeric
         metric, and a string would otherwise need a coercion step in a GTM
         custom-JS variable. The Elevar payload below keeps its strings —
         that schema wants them. */
      unit_price: Number((unitCents / 100).toFixed(2)),
      // Total, matching the GA4 convention for `value` on an add_to_cart.
      value: Number(((unitCents * qty) / 100).toFixed(2)),
      currency: this.currency || 'USD'
    }, this.planFields('', this.selectedSellingPlanGroup, this.selectedSellingPlan)))

    this.pushElevarAddToCart(quantity, cartResponse)
  }

  /* Ecommerce counterpart of the above — see SUBSCRIPTION_ELEVAR_TRACKING.
     Shape mirrors the payload in assets/addon-flow.js so both AJAX add paths
     report identically once enabled.

     VERIFY FIRST: `selling_plan` and `selling_plan_name` are additions to
     Elevar's standard product shape — everything else here matches a field
     Elevar documents. They are what makes a subscription add distinguishable
     inside the ecommerce rail, but Elevar rejects payloads carrying unknown
     fields, so confirm these two survive in the Elevar debugger before
     enabling. If they are rejected, drop just those two lines: the
     subscription dimension is still available on the GTM
     subscription_add_to_cart event above, which is unaffected. */
  pushElevarAddToCart (quantity, cartResponse) {
    if (!SUBSCRIPTION_ELEVAR_TRACKING.enabled || !window.ElevarDataLayer) return
    try {
      const line = (cartResponse?.items || [])[0]
      const variant = this.selectedVariant
      window.ElevarDataLayer.push({
        event: 'dl_add_to_cart',
        ecommerce: {
          currencyCode: this.currency || 'USD',
          add: {
            actionField: { list: window.location.pathname },
            products: [{
              id: line?.sku || String(variant?.id ?? ''),
              name: line?.product_title || this.product?.title || '',
              price: (this.subscriptionUnitPriceCents() / 100).toFixed(2),
              quantity: String(Number(quantity) || 1),
              product_id: String(this.product?.id ?? ''),
              variant_id: String(variant?.id ?? ''),
              variant: line?.variant_title || variant?.title || '',
              brand: line?.vendor || this.product?.vendor || '',
              category: line?.product_type || this.product?.type || '',
              selling_plan: String(this.selectedSellingPlan?.id ?? ''),
              selling_plan_name: this.selectedSellingPlan?.name ?? ''
            }]
          }
        }
      })
    } catch (elevarError) {
      this.error('Elevar subscription add-to-cart push failed: ', elevarError)
    }
  }

  addToCart (quantity = 1) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ]

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('SKIO added item to cart: ', response)

        /* Report only a genuine success. /cart/add.js answers 4xx with a JSON
           body carrying `description` / `status` instead of throwing, so an
           out-of-stock rejection would otherwise be counted as an add. */
        if (response && !response.status && !response.description) {
          this.trackAddToCart(quantity, response)
        }

        // dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent('skio::added-to-cart', {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key
          }
        })

        this.dispatchEvent(event)
      })
      .catch((error) => {
        this.error(`SKIO ${this.key} error adding item to cart: `, error)
      })
  }

  addToCartMeta () {
    const items = []

    const quantityEl = document.querySelector(`input[name="quantity"][form="${this.formId}"]`)
    let quantity = 1
    if (quantityEl) {
      quantity = quantityEl.value
    }

    const variants = this.meta.variants
    const variantMeta = variants.find(x => x.variantId === this.selectedVariant.id)

    const code = variantMeta.discount
    const meta = variantMeta.addToCart
    meta.forEach((item) => {
      const i = {
        id: item.variantId,
        quantity: parseInt(item.quantity) * quantity
      }
      if (item.selling_plan !== 'null' && item.selling_plan !== null) {
        i.selling_plan = parseInt(item.selling_plan)
      }
      items.push(i)
    })

    const cart = document.querySelector('cart-drawer')
    let sections
    let sectionsUrl
    if (cart) {
      sections = cart.getSectionsToRender().map((section) => section.id)
      sectionsUrl = window.location.pathname
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        items,
        ...(sections && { sections }),
        ...(sectionsUrl && { sectionsUrl })
      })
    })
      .then((response) => response.json())
      .then((response) => {
        this.log('SKIO META added item to cart: ', response)
        // dispatch CustomEvent to tell document that an item was added to cart
        const event = new CustomEvent('skio::added-to-cart', {
          bubbles: true,
          composed: true,
          detail: {
            response,
            key: this.key
          }
        })

        this.dispatchEvent(event)

        if (cart) {
          cart.renderContents(response)
          cart.classList.toggle('is-empty')
          cart.classList.toggle('active')
        }

        if (code) {
          window.location.href = '/checkout?discount=' + code
        } else {
          window.location.href = '/checkout'
        }
      })
      .catch((error) => {
        this.error(`SKIO META ${this.key} error adding item to cart: `, error)
      })
  }

  fetchProduct = (handle) => {
    return fetch(`/products/${handle}.js`)
      .then((response) => response.json())
      .then((product) => {
        this.product = product
        this.selectedVariant = product.variants[0]

        return product
      })
  }

  onSelectChange (event) {
    this.value = parseInt(event.target.value)
    this.input.value = this.value

    this.select.setAttribute('data-value', this.value)
    const options = this.select.querySelectorAll('option')
    options.forEach(option => {
      option.removeAttribute('selected')
    })

    const selectedOption = this.select.querySelector(`option[value="${this.value}"]`)
    if (selectedOption) {
      selectedOption.setAttribute('selected', 'selected')
    }

    // Update Skio quantity select if it exists
    const skioQuantitySelect = document.querySelector('.skio-quantity-select')
    if (skioQuantitySelect) {
      skioQuantitySelect.value = this.value
      skioQuantitySelect.dispatchEvent(new Event('change', { bubbles: true }))
    }

    const stickyQuantityInput = document.querySelector('.quantity__input-sticky')
    if (stickyQuantityInput) {
      stickyQuantityInput.value = this.value
      stickyQuantityInput.setAttribute('value', this.value)
    }

    const stickyQuantityInputMobile = document.querySelector('.quantity__input-sticky-mobile')
    if (stickyQuantityInputMobile) {
      stickyQuantityInputMobile.value = this.value
      stickyQuantityInputMobile.setAttribute('value', this.value)
    }
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
customElements.define('skio-plan-picker', SkioPlanPickerComponent)
